<template>
    <img :class="$style.orgLogo" :src="orgLogo" alt="Company logo" />
    <h1 :class="$style.jobTitle">
        {{ jobTitle }}
    </h1>
    <p :class="$style.orgName">
        {{ orgName }}
    </p>
    <Button
        :class="$style.applyButton"
        @clicked="openJobApplyPage"  
    >
        <template v-slot:buttonText>
            <span>Go to job page to apply</span>
        </template>
    </Button>
    <hr/>
    <div :class="$style.jobDatesSection">
        <div :class="$style.postedDate">
            <FontAwesomeIcon :icon="faCalendarDay" :class="$style.calendarIcon"/>
            <p>Posted<br/><b>{{ jobPostedAgo }}</b></p>
        </div>
        <div :class="$style.closingDate">
            <FontAwesomeIcon :icon="faClock" :class="$style.clockIcon" />
            <p>Closes<br/><b>{{ closingDate }}</b></p>
        </div>
    </div>
    <hr/>
    <div :class="$style.detailsContainer">
        <div :class="$style.detailsContainerLeft">
            <div :class="$style.jobDetailSection">
                <FontAwesomeIcon :icon="faBriefcase" :class="$style.faIcon" fixed-width />
                <div :class="$style.jobDetailSectionText">
                    <div :class="$style.detailHeader">Employment Type</div>
                    <div :class="$style.detailText">{{ jobEmploymentType }}</div>
                    <div :class="$style.detailSubText" v-if="jobEmploymentTypeDetails">
                        {{ jobEmploymentTypeDetails }}
                    </div>
                </div>
            </div>
            <div :class="$style.jobDetailSection" v-if="hasJobLocation">
                <FontAwesomeIcon :class="$style.faIcon" :icon="faMapMarkerAlt" fixed-width />
                <div :class="$style.jobDetailSectionText">
                    <div :class="$style.detailHeader">Location</div>
                    <div :class="$style.detailText">
                        <span 
                            v-if="jobRemotePreferences"
                            :class="$style.remotePreferences"
                        >
                            {{ jobRemotePreferences }}&nbsp;·&nbsp;
                        </span>
                        {{ jobLocationDescription }}
                    </div>
                    <div :class="$style.detailSubText">
                        {{ jobLocationDetails }}
                    </div>
                    <div :class="$style.detailSubText">
                        {{ jobLocationExtraDetails }}
                    </div>
                </div>
            </div>
            <div :class="$style.jobDetailSection" v-if="salary">
                <FontAwesomeIcon :class="$style.faIcon" :icon="faMoneyBill" fixed-width />
                <div :class="$style.jobDetailSectionText">
                    <div :class="$style.detailHeader">Salary</div>
                    <div :class="$style.detailText">{{ salary }}</div>
                    <div 
                        v-if="jobPackage !== salary"
                        :class="$style.detailSubText"
                    >
                        {{ jobPackage }}
                    </div>
                </div>
            </div>
        </div>
        <div :class="$style.detailsContainerRight">
            <div :class="$style.jobDetailSection" v-if="jobTeam && jobTeam.name">
                <FontAwesomeIcon :class="$style.faIcon" :icon="faUsers" fixed-width />
                <div :class="$style.jobDetailSectionText">
                    <div :class="$style.detailHeader">Team</div>
                    <div :class="$style.detailText">{{ jobTeam.name }}</div>
                </div>
            </div>
            <div v-if="seniority" :class="$style.jobDetailSection">
                <FontAwesomeIcon :class="$style.faIcon" :icon="faSortAmountUp" fixed-width />
                <div :class="$style.jobDetailSectionText">
                    <div :class="$style.detailHeader">Seniority</div>
                    <div :class="$style.detailText">{{ seniority }}</div>
                </div>
            </div>
        </div>
    </div>
    <hr/>
    <h3 :class="$style.jobDescriptionHeader">
        Job description
    </h3>
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div :class="$style.descText" v-html="jobDescription"></div>
</template>

<script>
import { Button } from '@applied/marmot'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faMapMarkerAlt, faMoneyBill  } from '@fortawesome/pro-solid-svg-icons'
import { faBriefcase, faUsers, faSortAmountUp, faCalendarDay, faClock } from '@fortawesome/free-solid-svg-icons'
import { currency, job as jobUtils } from 'sharedUtils'
import mapIcons from '@/utils/mapIcons'
import { format, formatDistanceToNow } from 'date-fns'
import sanitizeHTML from 'sanitize-html'

export default {
    name: 'JobDescription',
    components: {
        FontAwesomeIcon,
        Button,
    },
    props: {
        job: {
            type: Object,
            required: true,
        }
    },
    computed: {
        ...mapIcons(
            { 
                faCalendarDay, 
                faMapMarkerAlt, 
                faMoneyBill, 
                faBriefcase, 
                faUsers, 
                faSortAmountUp,
                faClock 
            }
        ),
        jobUrl() {
            return `/apply/${this.job.hash}?utm_source=appliedJobBoard`
        },
        orgLogo() {
            return this.job.orgLogo
        },
        jobDescription() {
            return this.job.text ? sanitizeHTML(this.job.text) : ''
        },
        jobTitle() {
            return this.job.title
        },
        orgName() {
            if (this.job.BrandId && this.job.Brand) {
                return this.job.Brand.name
            }

            return this.job.Org?.name
        },
        jobPostedAgo() {
            return formatDistanceToNow(new Date(this.job.createdAt), { addSuffix: true })
        },
        jobEmploymentType() {
            return this.job.JobEmploymentTypes?.length
                ? this.job.JobEmploymentTypes[0].EmploymentType.type
                : ''
        },
        jobEmploymentTypeDetails() {
            return this.job.employmentTypeDetails
        },
        jobRemotePreferences() {
            return this.job.remotePreferences
        },
        jobLocations() {
            return this.job.JobLocations?.map(
                jobLocation => jobLocation.Location
            )
        },
        jobLocationDescriptor() {
            return jobUtils.getJobLocationsDescriptor({
                locations: this.jobLocations,
                defaultDesc: this.job.location,
            })
        },
        hasJobLocation() {
            return !!this.jobLocationDescriptor.mainLocationDesc
        },
        jobLocationDescription() {
            return `${this.jobLocationDescriptor.mainLocationDesc} ${this.jobLocationDescriptor.otherLocationsDesc}`
        },
        jobLocationDetails() {
            const separator = ' · '
            return this.jobLocationDescriptor.formattedSublocationList.join(separator)
        },
        jobLocationExtraDetails() {
            return this.job.location !== this.jobLocationDescriptor.mainLocationDesc ? 
                this.job.location : ''
        },
        orderedJobSeniorities() {
            return this.job.JobSeniorities?.map(
                js => js.Seniority).sort(
                    (a, b) => a.order - b.order
            )
        },
        seniority() {
            if (!this.orderedJobSeniorities) {
                return null
            }

            return this.orderedJobSeniorities.map(seniority => seniority.name).join(', ')
        },
        hasSalary() {
            return (
                this.job.salaryCurrency && (this.job.salaryLowerBound || this.job.salaryUpperBound)
            )
        },
        hasSalaryDetails() {
            return Boolean(this.job.package)
        },
        salary() {
            if (this.hasSalary) {
                return currency.formatSalaryRange({
                    lowerBound: this.job.salaryLowerBound,
                    upperBound: this.job.salaryUpperBound,
                    currencyCode: this.job.salaryCurrency,
                    locale: 'en-GB',
                })
            }

            if (this.hasSalaryDetails) {
                return this.job.package
            }

            return ''
        },
        jobPackage() {
            return this.job.package
        },
        jobTeam() {
            return this.job.Team
        },
        closingDate() {
            return this.job?.closes ?
                format(new Date(this.job.closes), 'LLLL d, yyyy H:mm z') :
                'This role has no specific closing date.'
        },
    },
    methods: {
        openJobApplyPage() {
            window.open(this.jobUrl, '_blank')
        },
    },
}
</script>

<style module src="./JobDescription.css" />